/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { findLast } from 'lodash';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import useRouter from 'js/lib/useRouter';

import { Grid, Link, PageGridContainer, breakpoints, typography2 } from '@coursera/cds-core';
import type { MetaNavValue } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { onClickNavUrl } from 'bundles/cds-labs/components/Modules/utils/utm';
import { MOBILE_HEADER_BREAKPOINT } from 'bundles/composable-page-header/constants';
import { addUtmInfo } from 'bundles/composable-page-header/utils/metaNavUtils';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/page';

type Props = {
  isFluid?: boolean;
  isCustomGrid?: boolean;
};

export default function DefaultMetaNavigation({ isFluid, isCustomGrid }: Props) {
  const router = useRouter();
  const track = useTracker();

  const SOURCE_SANS = `'Source Sans Pro', Arial, sans-serif`;
  const NAV_ITEM_PADDING = '8px';
  const styles = {
    container: () => css`
      position: relative;
      top: 0;
      width: 100%;
      background: var(--cds-color-grey-975);
      z-index: 999;
      height: calc(40px + 16px);

      @media (max-width: ${MOBILE_HEADER_BREAKPOINT}px) {
        display: none;
      }

      ${breakpoints.down('md')} {
        height: calc(40px + 16px);
      }
      ${breakpoints.down('sm')} {
        height: calc(32px + 16px);
      }
      ${breakpoints.down('xs')} {
        height: calc(32px + 12px);
      }
    `,
    gridCustomContainer: css`
      padding-left: 16px !important;
      padding-right: 16px !important;
    `,
    fluidGridContainer: css`
      max-width: none;
    `,
    navListContainer: css`
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
    `,
    navScroller: css`
      overflow: hidden;
      height: 40px;
    `,
    navList: css`
      margin-left: 0;
      margin-right: -${NAV_ITEM_PADDING};
      margin-bottom: 0;
      padding-left: 0;
      display: flex;
      list-style: none;
      overflow-x: scroll;
    `,
    navListItemOuter: css`
      display: inherit;
    `,
    forStyle: () => css`
      color: var(--cds-color-grey-50);
      font-weight: 400;
    `,
    navListItem: () => css`
      border-bottom: 6px solid transparent;
      box-sizing: border-box;
      color: var(--cds-color-white-0) !important;
      margin: 0 ${NAV_ITEM_PADDING} !important;
      padding: 0 ${NAV_ITEM_PADDING} !important;
      font-family: ${SOURCE_SANS} !important;
      font-size: 16px !important;
      line-height: 40px !important;
      height: 40px;
      letter-spacing: ${typography2.bodyPrimary.letterSpacing};
      text-decoration: none !important;
      position: relative;

      &:hover,
      &:focus {
        background: var(--cds-color-white-0) !important;
        color: var(--cds-color-grey-975) !important;

        .forStyle {
          color: inherit;
        }
      }
    `,
    navListItemActive: () => css`
      border-bottom: 6px solid var(--cds-color-white-0);
      font-weight: 600 !important;
    `,
  };

  const generateMetaNavItems = () => [
    {
      key: 'Individuals',
      label: (
        <FormattedMessage
          message={_t('{for} Individuals')}
          for={
            <span className="forStyle" css={styles.forStyle}>
              {_t('For')}
            </span>
          }
        />
      ),
      link: `${config.url.base}`,
      domain: '',
      metaNavValue: 'for_individuals',
    },
    {
      key: 'Business',
      label: (
        <FormattedMessage
          message={_t('{for} Businesses')}
          for={
            <span className="forStyle" css={styles.forStyle}>
              {_t('For')}
            </span>
          }
        />
      ),
      link: `${config.url.base}business`,
      domain: '/business',
      metaNavValue: 'for_businesses',
    },
    {
      key: 'Campus',
      label: (
        <FormattedMessage
          message={_t('{for} Universities')}
          for={
            <span className="forStyle" css={styles.forStyle}>
              {_t('For')}
            </span>
          }
        />
      ),
      link: `${config.url.base}campus`,
      domain: '/campus',
      metaNavValue: 'for_universities',
    },
    {
      key: 'Government',
      label: (
        <FormattedMessage
          message={_t('{for} Governments')}
          for={
            <span className="forStyle" css={styles.forStyle}>
              {_t('For')}
            </span>
          }
        />
      ),
      link: `${config.url.base}government`,
      domain: '/government',
      metaNavValue: 'for_governments',
    },
  ];

  const onNavItemClick = (link: string, metaNavValue: MetaNavValue) => {
    track('click_meta_nav', {
      metaNavValue,
    });

    onClickNavUrl(router, link);
  };
  const pathname = router.location.pathname;
  const metaNavItems = generateMetaNavItems();
  const activeDomain = findLast(metaNavItems, (item) => pathname.indexOf(item.domain) === 0);

  return (
    <div css={styles.container}>
      <PageGridContainer
        maxWidth="lg"
        css={[isCustomGrid ? styles.gridCustomContainer : null, isFluid ? styles.fluidGridContainer : null]}
      >
        <Grid item xs={12}>
          <div css={styles.navListContainer}>
            <div role="navigation" aria-label={_t('Banner')} css={styles.navScroller}>
              <ul css={styles.navList}>
                {metaNavItems.map(({ key, label, link, domain, metaNavValue }) => {
                  const isActive = activeDomain?.domain === domain;
                  const trackingName = eventingTransform(`navigation_meta_nav_${key}`);
                  const modifiedLink = addUtmInfo(link, domain, router.location.pathname);
                  return (
                    <li css={styles.navListItemOuter} key={key}>
                      <Link
                        component={TrackedA}
                        trackingName={trackingName}
                        variant="quiet"
                        href={modifiedLink}
                        key={key}
                        onClick={() => onNavItemClick(modifiedLink, metaNavValue as MetaNavValue)}
                        aria-current={isActive ? 'page' : undefined}
                        css={[styles.navListItem, isActive ? styles.navListItemActive : null]}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
}
